<script setup lang="ts">
import { throttle } from "underscore";
import { useRouter } from "vue-router";
import { computed, onBeforeUnmount, onMounted, provide, ref } from "vue";

import { WINDOW_KEY } from "@/hooks/use-window";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";
import { getCasePanelComponent } from "@/hooks/use-case-panel";

import type { CaseRouteParams } from "@/hooks/use-case-route-params";
import type { IDockviewPanel, IDockviewPanelProps } from "dockview-vue";
import type { DockviewPanelComponentProps, OptionalPopoutRouteParams, PanelProps } from "@/hooks/use-case-panel";

withDefaults(defineProps<{ params: IDockviewPanelProps<PanelProps<boolean>> }>(), {  })

const router = useRouter();
const routeParams = useCaseRouteParams();

const key = ref<string>();
const openPopouts = ref<Array<{ panelId: string; windowId: string; window: Window }>>([]);

const windowRef = computed<Window>(() => props.value.window);

const panelComponent = computed(() => getCasePanelComponent(__props.params.params.panelType, props.value.isPopout));

const props = computed<NonNullable<DockviewPanelComponentProps<boolean>["dockview"]>>(() => {
  // Used only as a watched dep to trigger the re-render as the properties are not reactive
  void key.value;

  const window = __props.params.api.location.type === "popout" ? __props.params.api.location.getWindow() : globalThis.window;

  return {
    window,
    isVisible: __props.params.api.isVisible,
    isPopout: __props.params.api.location.type === "popout",
    params: __props.params.params,
    size: { width: __props.params.api.width || window.innerWidth, height: __props.params.api.height || window.innerHeight },
  };
});

provide(WINDOW_KEY, windowRef);

function runPanelPopoutAction(params: Pick<CaseRouteParams, OptionalPopoutRouteParams>) {
  if (props.value.isPopout) switchPopoutFileToMain(params);
  else openPopout(params);
}

function openPopout(popoutParams: Pick<CaseRouteParams, OptionalPopoutRouteParams>) {
  if (props.value.isPopout) return;

  if (routeParams.value.casefile == null) return; // Popouts must have a file attached to it

  const panelId = `${__props.params.api.id}-${routeParams.value.casename}-${routeParams.value.casefile}`;

  let popoutPanel: IDockviewPanel | undefined;
  // Check if popout for file has already been created
  const openPopoutState = openPopouts.value.find((p) => p.panelId === panelId);
  if (openPopoutState != null) {
    // If the window has already closed, then we cannot focus it
    if (!openPopoutState.window.closed) {
      openPopoutState.window.focus(); // Bring the window to view
      return;
    }

    // Get the previously created panel for this file popout
    popoutPanel = __props.params.containerApi.getPanel(openPopoutState.panelId);
  }

  popoutPanel ??= __props.params.containerApi.addPanel({
    id: panelId,
    component: __props.params.api.component,
    params: {
      ...__props.params.params,
      popoutRouteParams: {
        ...popoutParams,
        client: routeParams.value.client,
        brand: routeParams.value.brand,
        casename: routeParams.value.casename,
        casefile: routeParams.value.casefile,
      },
    } satisfies PanelProps<true>,
    inactive: true,
  });

  void __props.params.containerApi.addPopoutGroup(popoutPanel, {
    popoutUrl: __props.params.api.title,
    onDidOpen(event) {
      openPopouts.value.push({ panelId: popoutPanel.id, windowId: event.id, window: event.window });
    },
    onWillClose(event) {
      setTimeout(() => {
        const popout = openPopouts.value.find((p) => p.windowId === event.id);
        if (popout == null) return;

        __props.params.containerApi.getPanel(popout.panelId)?.api.close(); // Close created popout panel
        __props.params.api.setActive(); // Set Popout original panel as active panel
      });
    },
  });
}

function switchPopoutFileToMain(popoutCurrentParams: Pick<CaseRouteParams, OptionalPopoutRouteParams>) {
  if (!props.value.isPopout || props.value.params.popoutRouteParams == null) return;

  const currentCaseFile = routeParams.value.casefile;
  if (currentCaseFile == null) return;

  const { client, brand, casename, casefile: currentPopoutCaseFile } = props.value.params.popoutRouteParams;

  __props.params.api.updateParameters({
    panelType: props.value.params.panelType,
    popoutRouteParams: {
      // Optional Params
      filepage: routeParams.value.filepage,
      time: routeParams.value.time,
      // Required Params
      client,
      brand,
      casename,
      casefile: currentCaseFile,
    },
  } satisfies typeof params.params);

  router.push({
    name:
      popoutCurrentParams.filepage != null
        ? "casefilepage"
        : popoutCurrentParams.time != null
          ? "casefiletime"
          : "casefiletimerange",
    params: { ...popoutCurrentParams, brand, casename, casefile: currentPopoutCaseFile },
    force: true,
  });
}

const updateKey = throttle(() => (key.value = Math.random().toString(36)), 5);

onMounted(() => {
  updateKey();

  __props.params.api.onDidDimensionsChange(updateKey);
  __props.params.api.onDidVisibilityChange(updateKey);
  __props.params.api.onDidParametersChange(updateKey);
  __props.params.api.onDidLocationChange(updateKey);
  __props.params.api.onDidActiveChange(updateKey);
});

onBeforeUnmount(() => {
  openPopouts.value.forEach((w) => w.window.close());
});
</script>
<template>
  <component
    :is="panelComponent"
    v-if="panelComponent != null"
    :key="props.params.popoutRouteParams?.casefile"
    :dockview="props as never"
    @panel-popout-action="runPanelPopoutAction"
  />
</template>
